<script lang="ts">
  import { onMount, tick } from 'svelte';
  import { api, type Fixture } from '../lib/api/client.ts';

  interface Props {
    /** Adds the match as an angle, on the channel the user picked. */
    onAdd: (fixture: Fixture, channelId: number, channelName: string) => void;
    /** Channel ids already in the draft, so added matches can be marked. */
    usedChannelIds: number[];
    /** Heading; the watch menu calls it "Spiel hinzufügen". */
    title?: string;
    /**
     * Always ask which channel, even when the listings name one: while
     * watching there is no editor afterwards to correct the pick.
     */
    alwaysChoose?: boolean;
    /** Offers "another channel" for the match, picked from the full list. */
    onOther?: (fixture: Fixture) => void;
  }
  const { onAdd, usedChannelIds, title = 'Live & demnächst', alwaysChoose = false, onOther }: Props = $props();

  interface Suggestion { channelId: number; name: string; reason: string; confidence: number }

  /*
   * The last list is kept, so the page shows it at once and swaps in the fresh
   * one when it arrives. Older than a few hours it says nothing useful.
   */
  const CACHE_KEY = 'multiview.fixturesCache';
  const CACHE_MAX_AGE_MS = 6 * 3600_000;

  function cached(): Fixture[] | null {
    try {
      const raw = JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null') as { at: number; fixtures: Fixture[] } | null;
      if (raw && Array.isArray(raw.fixtures) && Date.now() - raw.at < CACHE_MAX_AGE_MS) return raw.fixtures;
    } catch { /* storage unavailable or garbage */ }
    return null;
  }

  const initial = cached();
  let fixtures = $state<Fixture[]>(initial ?? []);
  /** Until the first answer: the list takes seconds, and an empty page looks stuck. */
  let loaded = $state(initial !== null);
  /** Crests that failed to load; the name stands alone then. */
  let brokenLogos = $state(new Set<string>());
  let busy = $state<string | null>(null);
  let suggestions = $state<Record<string, Suggestion[]>>({});
  let notes = $state<Record<string, string>>({});
  /** The fixture whose channel choice or message is shown under the strip. */
  let active = $state<string | null>(null);
  const activeFixture = $derived(fixtures.find((f) => key(f) === active) ?? null);

  /**
   * Matches worth showing: running now, kicking off within three hours, or --
   * when the TV listings say where -- within the next day, so tonight's match
   * can be set up in the afternoon.
   */
  const SOON_MS = 3 * 3600_000;
  const AHEAD_MS = 24 * 3600_000;

  const candidates = $derived(
    fixtures.filter((f) => f.status === 'live' || (f.status === 'scheduled' && (
      f.kickoff - Date.now() < SOON_MS
      || (f.kickoff - Date.now() < AHEAD_MS && (f.channel != null || (f.broadcasters?.length ?? 0) > 0))
    ))),
  );

  /** Display order of the sports, and their names for the filter. */
  const SPORTS: Array<{ id: string; label: string }> = [
    { id: 'soccer', label: 'Fußball' },
    { id: 'basketball', label: 'Basketball' },
    { id: 'hockey', label: 'Eishockey' },
    { id: 'football', label: 'Football' },
    { id: 'baseball', label: 'Baseball' },
  ];
  const sportRank = (s: string) => { const i = SPORTS.findIndex((x) => x.id === s); return i < 0 ? 99 : i; };

  /** Filter and order are kept per device, like the ticker's choice. */
  const PREFS_KEY = 'multiview.fixtures';
  const saved = (() => {
    try { return JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}') as { sport?: string; order?: string }; } catch { return {}; }
  })();
  let sportFilter = $state<string>(saved.sport ?? 'all');
  let order = $state<'time' | 'sport'>(saved.order === 'sport' ? 'sport' : 'time');
  $effect(() => {
    localStorage.setItem(PREFS_KEY, JSON.stringify({ sport: sportFilter, order }));
  });

  /** Only sports that have a match to show get a filter button. */
  const presentSports = $derived(SPORTS.filter((s) => candidates.some((f) => f.sport === s.id)));

  const relevant = $derived(
    candidates
      .filter((f) => sportFilter === 'all' || f.sport === sportFilter)
      .sort((a, b) => {
        // Running matches first, then the next to kick off.
        if ((a.status === 'live') !== (b.status === 'live')) return a.status === 'live' ? -1 : 1;
        if (order === 'sport' && a.sport !== b.sport) return sportRank(a.sport) - sportRank(b.sport);
        return a.kickoff - b.kickoff;
      })
      .slice(0, 30),
  );

  const key = (f: Fixture) => `${f.provider}:${f.externalId}`;

  const SPORT_ICONS: Record<string, string> = {
    soccer: '⚽', basketball: '🏀', hockey: '🏒', football: '🏈', baseball: '⚾',
  };

  const kickoffLabel = (f: Fixture) => {
    const d = new Date(f.kickoff);
    const minutes = Math.round((f.kickoff - Date.now()) / 60000);
    if (f.status === 'live') return 'läuft';
    if (minutes <= 0) return 'gleich';
    if (minutes < 90) return `in ${minutes} min`;
    const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    return d.toDateString() === new Date().toDateString() ? time : `morgen ${time}`;
  };

  async function load() {
    try {
      fixtures = (await api.fixtures()).fixtures;
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), fixtures }));
      } catch { /* storage full or unavailable */ }
    } catch {
      // The start page must still work without a score provider.
    } finally {
      loaded = true;
    }
  }

  onMount(() => {
    void load();
    // Status and score change during a match; an hour-old list is misleading.
    const timer = setInterval(load, 60_000);
    return () => clearInterval(timer);
  });

  /**
   * One click should be enough, so the channel is looked up straight away. Only
   * when the answer is ambiguous does it ask.
   */
  async function pick(f: Fixture) {
    const k = key(f);
    // Not `disabled` while busy: that would drop the remote's focus to <body>.
    if (busy === k) return;

    // Known in advance from the TV listings: no lookup, no question.
    if (f.channel && !alwaysChoose) {
      active = k;
      notes = { ...notes, [k]: `auf ${f.channel.name}` };
      onAdd(f, f.channel.id, f.channel.name);
      return;
    }
    busy = k;
    active = k;
    notes = { ...notes, [k]: '' };
    try {
      const r = await api.fixtureChannels(f.provider, f.externalId);
      if (alwaysChoose) {
        const list: Suggestion[] = r.suggestions;
        // The listings' pick leads, even if the lookup ranks it differently.
        if (f.channel) {
          const known = f.channel;
          list.sort((a, b) => Number(b.channelId === known.id) - Number(a.channelId === known.id));
          if (!list.some((s) => s.channelId === known.id)) {
            list.unshift({ channelId: known.id, name: known.name, reason: known.reason, confidence: 1 });
          }
        }
        suggestions = { ...suggestions, [k]: list };
        if (list.length === 0) {
          notes = { ...notes, [k]: r.epgAvailable ? 'Kein Sender im EPG gefunden.' : 'Keine EPG-Daten für diesen Zeitraum.' };
        }
        // On the remote the choice would otherwise sit unreached below the strip.
        await tick();
        document.querySelector<HTMLElement>('.live .detail [data-focusable]')?.focus();
        return;
      }
      if (r.suggestions.length === 1) {
        const only = r.suggestions[0]!;
        onAdd(f, only.channelId, only.name);
        notes = { ...notes, [k]: `auf ${only.name}` };
        return;
      }
      if (r.suggestions.length === 0) {
        notes = {
          ...notes,
          [k]: r.epgAvailable
            ? 'Kein Sender im EPG gefunden — Kanal unter „Neues Match“ selbst wählen.'
            : 'Keine EPG-Daten für diesen Zeitraum.',
        };
        return;
      }
      suggestions = { ...suggestions, [k]: r.suggestions };
    } catch (err) {
      notes = { ...notes, [k]: err instanceof Error ? err.message : String(err) };
    } finally {
      busy = null;
    }
  }

  function choose(f: Fixture, s: Suggestion) {
    const k = key(f);
    suggestions = { ...suggestions, [k]: [] };
    notes = { ...notes, [k]: `auf ${s.name}` };
    onAdd(f, s.channelId, s.name);
  }
</script>

{#snippet crest(logo: string | null | undefined)}
  {#if logo && !brokenLogos.has(logo)}
    <img class="crest" src={logo} alt="" loading="lazy" onerror={() => { brokenLogos = new Set(brokenLogos).add(logo); }} />
  {/if}
{/snippet}

{#if !loaded}
  <section class="live" aria-busy="true">
    <div class="head">
      <h2><span class="spinner"></span> Spiele werden geladen …</h2>
    </div>
    <ul class="strip" aria-hidden="true">
      {#each [0, 1, 2, 3] as i (i)}
        <li><div class="match skeleton"><span class="bar short"></span><span class="bar"></span><span class="bar mid"></span></div></li>
      {/each}
    </ul>
  </section>
{:else if candidates.length > 0}
  <section class="live">
    <div class="head">
      <h2>{title} <span class="faint">{relevant.length}</span></h2>
      {#if presentSports.length > 1}
        <div class="chips" role="group" aria-label="Sportart">
          <button data-focusable class:on={sportFilter === 'all'} onclick={() => { sportFilter = 'all'; }}>Alle</button>
          {#each presentSports as s (s.id)}
            <button data-focusable class:on={sportFilter === s.id} onclick={() => { sportFilter = s.id; }}>
              {SPORT_ICONS[s.id] ?? ''} {s.label}
            </button>
          {/each}
        </div>
        {#if sportFilter === 'all'}
          <div class="chips" role="group" aria-label="Sortierung">
            <button data-focusable class:on={order === 'time'} onclick={() => { order = 'time'; }}>nach Zeit</button>
            <button data-focusable class:on={order === 'sport'} onclick={() => { order = 'sport'; }}>nach Sportart</button>
          </div>
        {/if}
      {/if}
    </div>
    {#if relevant.length === 0}
      <p class="faint none">Gerade nichts in dieser Sportart.</p>
    {/if}

    <ul class="strip scroll-x">
      {#each relevant as f (key(f))}
        {@const k = key(f)}
        <li class:running={f.status === 'live'} class:active={active === k}>
          <button
            class="match"
            data-focusable
            aria-busy={busy === k}
            onclick={() => pick(f)}
            title="Spiel als Perspektive hinzufügen"
          >
            <span class="line">
              <span class="when">{busy === k ? '…' : kickoffLabel(f)}</span>
              {#if f.status === 'live' && f.homeScore !== null}
                <strong class="score">{f.homeScore}:{f.awayScore}</strong>
              {/if}
              <span class="comp">{SPORT_ICONS[f.sport] ?? ''} {f.competition}</span>
            </span>
            <span class="teams">
              {@render crest(f.homeLogo)}<span class="team">{f.homeTeam}</span>
              <span class="faint">–</span>
              {@render crest(f.awayLogo)}<span class="team">{f.awayTeam}</span>
            </span>
            {#if f.channel}
              <span class="tv on" title={f.channel.reason}>▶ {f.channel.name}</span>
            {:else if f.broadcasters?.length}
              <span class="tv">{f.broadcasters.slice(0, 3).join(' · ')}{f.broadcasters.length > 3 ? ` +${f.broadcasters.length - 3}` : ''}</span>
            {/if}
          </button>
        </li>
      {/each}
    </ul>

    {#if activeFixture && (notes[active!] || suggestions[active!]?.length || (onOther && busy !== active))}
      <div class="detail">
        {#if suggestions[active!]?.length}
          <span class="faint">Welcher Sender zeigt {activeFixture.homeTeam} – {activeFixture.awayTeam}?</span>
          {#each suggestions[active!]! as s (s.channelId)}
            <button
              data-focusable
              class:used={usedChannelIds.includes(s.channelId)}
              title={s.reason}
              onclick={() => choose(activeFixture, s)}
            >
              {s.name}
              <span class="faint">{Math.round(s.confidence * 100)}%</span>
            </button>
          {/each}
        {:else if notes[active!]}
          <span class="faint">{notes[active!]}</span>
        {/if}
        {#if onOther && busy !== active}
          <button data-focusable onclick={() => onOther(activeFixture)}>Anderer Sender …</button>
        {/if}
      </div>
    {/if}
  </section>
{/if}

<style>
  .live {
    background: var(--bg-raised);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: calc(var(--pad) * 0.6) var(--pad);
    display: flex;
    flex-direction: column;
    gap: calc(var(--gap) * 0.75);
    min-width: 0;
  }

  h2 { margin: 0; font-size: var(--font-base); color: var(--text-dim); font-weight: 600; }

  .head { display: flex; flex-wrap: wrap; align-items: center; gap: var(--gap); }
  .chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .chips button { font-size: var(--font-sm); padding: 0.2em 0.7em; }
  .chips button.on { border-color: var(--accent); color: var(--accent); }
  .none { margin: 0; font-size: var(--font-sm); }

  /* One row only: the games are a shortcut, not the point of the page. */
  .strip {
    list-style: none;
    margin: 0;
    /* Room for the focus ring, which the scroll container would clip. */
    padding: 6px;
    margin: -6px;
    display: flex;
    gap: var(--gap);
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: thin;
    scrollbar-color: var(--border-strong) transparent;
  }

  .strip li { flex: 0 0 auto; }

  .match {
    width: 17em;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    text-align: left;
    padding: 0.4em 0.7em;
    font-size: var(--font-sm);
    border-left: 3px solid transparent;
  }

  li.running .match { border-left-color: var(--accent); }
  li.active .match { border-color: var(--accent); }

  .line { display: flex; gap: 0.5em; align-items: baseline; max-width: 100%; min-width: 0; }
  .when { color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.04em; flex: 0 0 auto; }
  li.running .when { color: var(--accent); }
  .score { color: var(--accent); font-variant-numeric: tabular-nums; flex: 0 0 auto; }
  .comp { color: var(--text-faint); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }

  .teams {
    display: flex;
    align-items: center;
    gap: 0.3em;
    font-weight: 600;
    font-size: var(--font-base);
    white-space: nowrap;
    max-width: 100%;
    min-width: 0;
  }
  /* Both names share the room; a long one shortens rather than pushing the other out. */
  .team { overflow: hidden; text-overflow: ellipsis; min-width: 0; flex: 0 1 auto; }
  .crest { width: 1.3em; height: 1.3em; object-fit: contain; flex: none; }

  .tv {
    color: var(--text-faint);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  .tv.on { color: var(--accent); }

  h2 .spinner { display: inline-block; vertical-align: -0.15em; margin-right: 0.4em; }
  .spinner {
    width: 1em; height: 1em;
    border: 2px solid var(--border-strong);
    border-top-color: var(--text);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Placeholders the size of a match card, so nothing jumps when they come. */
  .skeleton {
    gap: 0.45em;
    padding: 0.6em 0.7em;
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }
  .bar {
    display: block;
    height: 0.8em;
    width: 100%;
    border-radius: 0.3em;
    background: var(--border);
    animation: glow 1.4s ease-in-out infinite;
  }
  .bar.short { width: 35%; }
  .bar.mid { width: 60%; }
  @keyframes glow { 50% { opacity: 0.4; } }

  .detail { display: flex; flex-wrap: wrap; gap: var(--gap); align-items: center; font-size: var(--font-sm); }
  .detail button { font-size: var(--font-sm); }
  .detail button.used { border-color: var(--accent); }
</style>
